import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { WebOnlyAdmin } from "@/desktop/web-only";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router";
import { AppShell } from "@/components/app-shell";
import { FullPageSpinner } from "@/components/feedback";
import { desktop } from "@/desktop/bridge";
import { DesktopOnly } from "@/desktop/desktop-only";
import { FULL_EDITION, type DesktopFeature } from "@/desktop/edition";
import { RootLayout } from "@/desktop/root";
import { Spinner } from "@/components/ui/primitives";
import { onUnauthorized } from "@/lib/api";
import { DOWNLOAD_URL } from "@/lib/landing";
import { keys, queryClient, useMe } from "@/lib/queries";
import { useUiTheme } from "@/plugins/use-plugins";
import { useNotificationToasts } from "@/components/notifications";

const LoginPage = lazy(() => import("@/pages/login"));
const TermsPage = lazy(() => import("@/pages/terms"));
const OnboardingPage = lazy(() => import("@/pages/onboarding"));
const ProjectsPage = lazy(() => import("@/pages/projects"));
const LocalFolderPage = lazy(() => import("@/pages/local-folder"));
const WorkspacePage = lazy(() => import("@/workspace/workspace-page"));
const ReplayPage = lazy(() => import("@/pages/replay"));
const InvitePage = lazy(() => import("@/pages/invite"));
const DiscoverPage = lazy(() => import("@/pages/discover"));
const ProfilePage = lazy(() => import("@/pages/profile"));
const GroupsPage = lazy(() => import("@/pages/groups"));
const GroupPage = lazy(() => import("@/pages/group"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const AdminPage = lazy(() => import("@/pages/admin"));
const PluginsPage = lazy(() => import("@/pages/plugins"));
const ChatPage = lazy(() => import("@/pages/chat"));
const NotFoundPage = lazy(() => import("@/pages/not-found"));

/** Recurso do produto completo: no app desktop abre; na web, mostra o convite para o app. */
const only = (feature: DesktopFeature, page: ReactNode) => (
  <DesktopOnly feature={feature}>{page}</DesktopOnly>
);

/**
 * Rotas que exigem sessão. Sem conta vai para a tela de entrada (no app desktop também: o Kompil só
 * abre depois de entrar ou criar a conta) e volta depois. Quem ainda não respondeu as perguntas de
 * boas-vindas passa por elas uma vez.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) return <FullPageSpinner />;
  const here = encodeURIComponent(location.pathname + location.search);
  // Convidado de versões antigas do app: cria a conta (os projetos dele vão junto).
  if (!me || (desktop && me.user.isGuest)) return <Navigate to={`/login?next=${here}`} replace />;
  if (me.user.onboarded === false && !me.user.isGuest)
    return <Navigate to={`/boas-vindas?next=${here}`} replace />;
  return <>{children}</>;
}

/** "/" (ao abrir o app, ao entrar na conta) leva sempre para a tela de projetos. */
function HomeRedirect() {
  const { data: me } = useMe();
  if (!me) return <FullPageSpinner />;
  return <Navigate to="/projects" replace />;
}

/**
 * Endereço antigo da página de download: ela agora fica no site do Kompil (a landing), com os
 * instaladores e o hash de cada sistema. Links antigos (e-mails, convites) continuam funcionando.
 */
function DownloadRedirect() {
  useEffect(() => window.location.replace(DOWNLOAD_URL), []);
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 text-[13px] text-fg-muted">
      <Spinner />
      <a href={DOWNLOAD_URL} className="underline underline-offset-4 hover:text-fg">
        Abrir a página de download do Kompil
      </a>
    </div>
  );
}

/**
 * "/": na web, quem não entrou vai para a entrada (a apresentação do produto e o download ficam no
 * site do Kompil); quem entrou vai para os projetos. O app desktop nunca mostra a entrada aqui.
 */
function RootIndex() {
  const { data: me, isLoading } = useMe();
  if (desktop)
    return (
      <RequireAuth>
        <HomeRedirect />
      </RequireAuth>
    );
  if (isLoading) return <FullPageSpinner />;
  if (!me || me.user.isGuest) return <Navigate to="/login" replace />;
  return <HomeRedirect />;
}

function Shell() {
  return (
    <RequireAuth>
      <AppShell>
        <Suspense fallback={<FullPageSpinner />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </RequireAuth>
  );
}

function ProjectWorkspaceRoute() {
  const { search } = useLocation();
  return new URLSearchParams(search).has("localFolder") ? <LocalFolderPage /> : <WorkspacePage />;
}

function Bare({ auth = true }: { auth?: boolean }) {
  const content = (
    <Suspense fallback={<FullPageSpinner />}>
      <Outlet />
    </Suspense>
  );
  return auth ? <RequireAuth>{content}</RequireAuth> : content;
}

const router = createBrowserRouter([
  {
    // Raiz: no app desktop, a barra de título no estilo do VS Code fica acima de todas as páginas.
    element: <RootLayout />,
    children: [
      {
        element: <Bare auth={false} />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/legal", element: <TermsPage /> },
          { path: "/boas-vindas", element: <OnboardingPage /> },
          { path: "/invite/:token", element: <InvitePage /> },
          { path: "/download", element: <DownloadRedirect /> },
          { path: "/", element: <RootIndex /> },
        ],
      },
      {
        element: <Bare />,
        children: [
          { path: "/p/:projectId", element: <ProjectWorkspaceRoute /> },
          { path: "/local-folder/:folderId", element: <LocalFolderPage /> },
          {
            path: "/p/:projectId/replay/:sessionId",
            element: <div className="px-5 py-10">{only("history", <ReplayPage />)}</div>,
          },
        ],
      },
      {
        element: <Shell />,
        children: [
          { path: "/projects", element: <ProjectsPage /> },
          { path: "/chat", element: <ChatPage /> },
          { path: "/chat/:conversationId", element: <ChatPage /> },
          { path: "/discover", element: only("network", <DiscoverPage />) },
          { path: "/u/:handle", element: only("network", <ProfilePage />) },
          { path: "/groups", element: only("groups", <GroupsPage />) },
          { path: "/g/:slug", element: only("groups", <GroupPage />) },
          { path: "/settings", element: only("settings", <SettingsPage />) },
          // Administração: só na plataforma web (no app, o aviso com o link para o navegador).
          {
            path: "/admin",
            element: (
              <WebOnlyAdmin>
                <AdminPage />
              </WebOnlyAdmin>
            ),
          },
          { path: "/plugins", element: only("plugins", <PluginsPage />) },
          // O Construtor IA saiu: Claude e GitHub Copilot vivem nos plugins (canto superior direito).
          { path: "/build", element: <Navigate to="/projects" replace /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

/** Tema de cor instalado como plugin (interface inteira + código), em qualquer página. */
function UiThemeBridge() {
  const { data: me } = useMe();
  // Temas de cor são plugins: produto completo (app desktop). A web fica no preto e branco.
  useUiTheme(Boolean(me) && FULL_EDITION);
  // Notificação nova vira um aviso na hora em qualquer tela (inclusive com um projeto aberto).
  useNotificationToasts(Boolean(me) && !me?.user.isGuest);
  return null;
}

export function App() {
  // Sessão expirada em qualquer chamada → refaz /me (e a guarda leva ao login). Só se havia sessão:
  // sem ela, o próprio /me responde 401 e refazê-lo a cada 401 virava um laço infinito.
  useEffect(
    () =>
      onUnauthorized(() => {
        if (queryClient.getQueryData(keys.me)) void queryClient.invalidateQueries({ queryKey: keys.me });
      }),
    [],
  );
  return (
    <>
      <UiThemeBridge />
      <RouterProvider router={router} />
    </>
  );
}
